import axios from 'axios';

// API Base URL - set REACT_APP_API_BASE_URL in frontend/.env
const _BASE = process.env.REACT_APP_API_BASE_URL;
const API_BASE_URL = `${_BASE}/api.php`;
const INFO_API_URL = `${_BASE}/info.php`;
const LANGUAGES_API_URL = `${_BASE}/languages.php`;

// ----------------------------------------------------------------
// Axios interceptor: attaches the auth token to every request
// ----------------------------------------------------------------
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ----------------------------------------------------------------
// Axios interceptor: fires a global event on 401
// so that AuthContext can log out the user.
// ----------------------------------------------------------------
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

/**
 * API Service for CRUD operations
 */
const apiService = {
  /**
   * Fetch all records
   */
  getAll: async (table = 'eu') => {
    try {
      const response = await axios.get(`${API_BASE_URL}?source=${table}`);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Fetch single record
   */
  getById: async (id, table = 'eu') => {
    try {
      const response = await axios.get(`${API_BASE_URL}?id=${id}&source=${table}`);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Create new record
   */
  create: async (data, table = 'eu') => {
    try {
      const response = await axios.post(`${API_BASE_URL}?source=${table}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Update record
   */
  update: async (id, data, table = 'eu') => {
    try {
      const response = await axios.put(`${API_BASE_URL}?id=${id}&source=${table}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Fetch info text
   */
  getInfo: async () => {
    try {
      const response = await axios.get(INFO_API_URL);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Save info text
   */
  saveInfo: async (text) => {
    try {
      const response = await axios.post(INFO_API_URL, { text }, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Fetch language JSON
   */
  getLanguage: async (lang) => {
    try {
      const response = await axios.get(`${LANGUAGES_API_URL}?lang=${lang}`);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Save language JSON
   */
  saveLanguage: async (lang, text) => {
    try {
      const response = await axios.post(`${LANGUAGES_API_URL}?lang=${lang}`, { text }, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Delete record
   */
  delete: async (id, table = 'eu') => {
    try {
      const response = await axios.delete(`${API_BASE_URL}?id=${id}&source=${table}`);
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },
};

/**
 * Error handling
 */
const handleError = (error) => {
  if (error.response) {
    // Server responded with an error code
    return {
      message: error.response.data.error || 'Ein Fehler ist aufgetreten',
      status: error.response.status,
    };
  } else if (error.request) {
    // Request was sent but no response received
    return {
      message: 'Keine Verbindung zum Server. Bitte prüfen Sie, ob XAMPP läuft.',
      status: 0,
    };
  } else {
    // Error while creating the request
    return {
      message: error.message || 'Ein unbekannter Fehler ist aufgetreten',
      status: -1,
    };
  }
};

export default apiService;
